import React, { useEffect, useMemo, useState } from "react";
import { Alert, Form, Placeholder, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRotateRight } from "@fortawesome/free-solid-svg-icons";
import { crmPostventaV2Api } from "../../crmpostventa-v2/crmPostventaV2Api";
import AlertFilters from "../AlertFilters";
import AlertScrollArea from "../AlertScrollArea";
import {
  applyAlertFilters,
  buildAlertFilterOptions,
  EMPTY_ALERT_FILTERS,
  hasActiveAlertFilters,
} from "../alertFiltersUtils";
import OnboardingAlertCard from "./OnboardingAlertCard";
import { filterOnboardingRows, normalizeOnboardingAlertItem, normalizeText } from "./onboardingAlertsUtils";
import "./onboarding-alerts.css";

const FETCH_PAGE_SIZE = 200;
const MOBILE_BREAKPOINT = 900;
const SEARCH_DEBOUNCE_MS = 300;
const emptyFilters = { search: "", date: "" };

export default function OnboardingAlertsSection({
  processCode,
  processSlug,
  title,
  emptyMessage,
  globalFilters,
  onManage,
  onMutationNonce = 0,
}) {
  const [filters, setFilters] = useState(emptyFilters);
  const [alertFilters, setAlertFilters] = useState(EMPTY_ALERT_FILTERS);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [state, setState] = useState({ loading: true, rows: [], total: 0, error: "", permissions: null });
  const [viewportMobile, setViewportMobile] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth <= MOBILE_BREAKPOINT : false
  );

  useEffect(() => {
    const timeout = window.setTimeout(() => setDebouncedSearch(filters.search), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timeout);
  }, [filters.search]);

  useEffect(() => {
    const onResize = () => setViewportMobile(window.innerWidth <= MOBILE_BREAKPOINT);
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const loadRows = async () => {
    setState((prev) => ({ ...prev, loading: true, error: "" }));
    try {
      let pageNumber = 1;
      let total = 0;
      let mergedRows = [];

      while (pageNumber === 1 || mergedRows.length < total) {
        const response = await crmPostventaV2Api.obtenerAlertas({
          procesoCodigo: processCode,
          fechaReferencia: globalFilters?.fechaReferencia,
          prioridad: globalFilters?.prioridad || undefined,
          estado: globalFilters?.estado || undefined,
          texto: globalFilters?.texto || undefined,
          pagina: pageNumber,
          tamanoPagina: FETCH_PAGE_SIZE,
        });

        const currentRows = Array.isArray(response?.filas) ? response.filas : [];
        total = Number(response?.total || currentRows.length || 0);
        mergedRows = mergedRows.concat(currentRows);

        if (!currentRows.length || mergedRows.length >= total) {
          setState({
            loading: false,
            rows: mergedRows.map((item) => normalizeOnboardingAlertItem({ ...item, procesoSlug: processSlug })),
            total,
            permissions: response?.permisos || null,
            error: "",
          });
          return;
        }

        pageNumber += 1;
      }
    } catch {
      setState({
        loading: false,
        rows: [],
        total: 0,
        permissions: null,
        error: "No pudimos cargar los procesos de onboarding.",
      });
    }
  };

  useEffect(() => {
    loadRows();
  }, [globalFilters?.estado, globalFilters?.fechaReferencia, globalFilters?.prioridad, globalFilters?.texto, processCode, processSlug]);

  useEffect(() => {
    if (onMutationNonce > 0) loadRows();
  }, [onMutationNonce]);

  const filteredRows = useMemo(
    () => applyAlertFilters(filterOnboardingRows(state.rows, { ...filters, search: debouncedSearch }), alertFilters),
    [alertFilters, debouncedSearch, filters, state.rows]
  );

  const filterOptions = useMemo(() => buildAlertFilterOptions(state.rows), [state.rows]);

  const hasActiveFilters = useMemo(
    () =>
      Object.values(filters).some((value) => Boolean(normalizeText(value))) ||
      hasActiveAlertFilters("", alertFilters),
    [alertFilters, filters]
  );

  const resetFilters = () => {
    setFilters(emptyFilters);
    setAlertFilters(EMPTY_ALERT_FILTERS);
  };

  return (
    <section className="onboarding-alerts-section">
      <div className="onboarding-alerts__header">
        <div className="onboarding-alerts__title-block">
          <h3>{title}</h3>
          <p>Alta, checklist obligatorio y derivación inicial.</p>
        </div>

        <div className="onboarding-alerts__header-actions">
          <button type="button" className="crm-alert-refresh-icon" onClick={loadRows} disabled={state.loading} aria-label="Actualizar onboarding">
            {state.loading ? <Spinner animation="border" size="sm" /> : <FontAwesomeIcon icon={faRotateRight} />}
          </button>
        </div>
      </div>

      <AlertFilters
        className="onboarding-alerts__filters"
        search={filters.search}
        onSearchChange={(value) => setFilters((prev) => ({ ...prev, search: value }))}
        searchAriaLabel="Buscar procesos de onboarding"
        filters={alertFilters}
        onFiltersChange={setAlertFilters}
        options={filterOptions}
        onReset={resetFilters}
        hasActiveFilters={hasActiveFilters}
        hiddenKeys={["status"]}
      >
        <Form.Control
          type="date"
          className="crm-alert-filters__date"
          value={filters.date}
          onChange={(event) => setFilters((prev) => ({ ...prev, date: event.target.value }))}
          aria-label="Filtrar onboarding por fecha de próxima acción"
        />
      </AlertFilters>

      {!viewportMobile ? (
        <div className="onboarding-alerts__table-head">
          <span>Comercio</span>
          <span>Seguimiento</span>
          <span>Próx. acción</span>
          <span>Prioridad</span>
          <span>Responsable</span>
          <span>Acciones</span>
        </div>
      ) : null}

      {state.loading ? (
        <AlertScrollArea viewportClassName="onboarding-alerts__list is-loading">
          {Array.from({ length: 3 }, (_, index) => (
            <div key={index} className="onboarding-alerts__skeleton-card">
              <Placeholder as="div" animation="glow"><Placeholder xs={12} /></Placeholder>
              <Placeholder as="div" animation="glow"><Placeholder xs={8} /></Placeholder>
            </div>
          ))}
        </AlertScrollArea>
      ) : state.error ? (
        <div className="crm-alert-load-error" role="alert">
          <span className="crm-alert-load-error__icon" aria-hidden="true">!</span>
          <span>No pudimos cargar los procesos de onboarding.</span>
        </div>
      ) : !state.rows.length ? (
        <div className="crm-alert-load-error" role="status">
          <span className="crm-alert-load-error__icon" aria-hidden="true">!</span>
          <span>{emptyMessage || "No hay procesos de onboarding pendientes."}</span>
        </div>
      ) : !filteredRows.length ? (
        <Alert variant="secondary" className="mb-0">No se encontraron procesos de onboarding con los filtros seleccionados.</Alert>
      ) : (
        <AlertScrollArea viewportClassName="onboarding-alerts__list">
          {filteredRows.map((item) => (
            <OnboardingAlertCard key={item.id} item={item} mobile={viewportMobile} busy={false} onManage={onManage} />
          ))}
        </AlertScrollArea>
      )}

      {state.permissions && !state.permissions?.puedeGestionarPostventaGeneral && !state.permissions?.puedeAdministrarPostventa ? (
        <div className="onboarding-alerts__permission-note">No tenés permisos para gestionar procesos de onboarding.</div>
      ) : null}

    </section>
  );
}
